<template>
  <div class="tw:bg-branding-000 tw:border-t tw:border-default-200 tw:w-full tw:py-[0.875rem] tw:flex-shrink-0">
    <div class="tw:flex tw:justify-evenly tw:items-center tw:h-full">
      <div
        class="tw:p-2 tw:rounded tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center"
        :style="isLiveEventsActive ? activeBackgroundStyle : ''"
        @click="handleNavigate('/app/live-events')"
      >
        <LiveEventsIcon :show-background="false" size="medium" />
      </div>
      <div
        class="tw:p-2 tw:rounded tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center"
        :style="isNewsletterHubActive ? activeBackgroundStyle : ''"
        @click="handleNavigate('/app/newsletters')"
      >
        <font-awesome-icon icon="fa-light fa-envelopes" class="tw:text-2xl" />
      </div>
      <div
        class="tw:p-2 tw:rounded tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center"
        :style="isInboxActive ? activeBackgroundStyle : ''"
        @click="handleNavigate('/app/inbox')"
      >
        <div class="tw:relative tw:!text-2xl" unique-id="main-menu__mobile-inbox-button">
          <font-awesome-icon icon="fa-light fa-inbox" />
          <EdroneBadge
            v-if="inboxBadgeText && inboxBadgeText !== '0'"
            :label="inboxBadgeText"
            type="info"
            class="tw:absolute tw:rounded-md tw:!bg-blue-500"
            solid
            size="small"
            style="top: -7px; right: -12px"
          />
        </div>
      </div>
      <div class="tw:p-2 tw:rounded tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center">
        <NotificationsMenu />
      </div>
      <div
        class="tw:p-2 tw:rounded tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center"
        @click="isMenuOpen = true"
      >
        <font-awesome-icon icon="fa-light fa-bars" class="tw:text-2xl" unique-id="main-menu__mobile-open-menu-button" />
      </div>
    </div>

    <Teleport to="body">
      <Transition
        appear
        enter-active-class="tw:transition-opacity tw:duration-300 tw:ease-in-out"
        enter-from-class="tw:opacity-0"
        leave-active-class="tw:transition-opacity tw:duration-300 tw:ease-in-out"
        leave-to-class="tw:opacity-0"
      >
        <div
          v-if="showMenu"
          class="tw:flex tw:items-end tw:fixed tw:h-full tw:w-full tw:top-0 tw:right-0 tw:m-0 tw:z-[41000] tw:bg-black/30 main-menu-mobile-backdrop"
          @click="
            isMenuOpen = false;
            gtagUniversalSend({ component_id: 'main-menu__mobile-close-menu' });
          "
        >
          <Transition
            enter-active-class="tw:transition-transform tw:duration-300 tw:ease-in-out"
            enter-from-class="tw:translate-y-full"
            leave-active-class="tw:transition-transform tw:duration-300 tw:ease-in-out"
            leave-to-class="tw:translate-y-full"
          >
            <div
              v-if="showContent"
              class="tw:w-full tw:bg-white tw:border tw:border-default-200 tw:flex tw:flex-col tw:gap-3 tw:py-3 tw:px-4 tw:overflow-y-auto tw:max-h-[80vh] tw:relative tw:z-10"
              style="
                box-shadow: 0px 0px 0.9375rem 0px rgba(0, 0, 0, 0.08);
                border-radius: 0.5rem 0.5rem 0 0;
                will-change: transform;
              "
              @click.stop
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <div class="tw:bg-default-200 tw:rounded-sm tw:mx-auto tw:h-1.5 tw:w-2/5 tw:min-h-1.5" />

              <div class="tw:flex tw:flex-col tw:flex-1 tw:min-h-0">
                <!-- Main Menu View -->
                <template v-if="!showSettingsMenu && !showWorkspaceMenu">
                  <div class="tw:overflow-y-auto tw:flex-1">
                    <div class="tw:flex tw:flex-col">
                      <MainMenuMobileLinkList
                        :list="mainTree"
                        :expanded-items="expandedItems"
                        :menu-badges="menuData"
                        @click="handleClickMenuItem($event)"
                        @toggle="handleToggleItem($event)"
                      />
                    </div>
                  </div>

                  <div class="tw:flex-shrink-0 tw:mt-3">
                    <MainMenuSettingsButton @click="handleClickSettingsButton" />
                  </div>
                </template>

                <!-- Settings Menu View -->
                <template v-else-if="showSettingsMenu">
                  <div class="tw:overflow-y-auto tw:flex-1">
                    <div class="tw:flex tw:flex-col">
                      <div class="tw:flex tw:items-center tw:py-2 tw:mb-3">
                        <EdroneButton type="text" size="small" @click="handleBackToMainMenu">
                          <template #left-icon>
                            <font-awesome-icon icon="fa-light fa-arrow-left" />
                          </template>
                          {{ trans["Back"] }}
                        </EdroneButton>
                      </div>

                      <MainMenuMobileLinkList
                        :list="settingsItems"
                        :expanded-items="expandedItems"
                        :menu-badges="menuData"
                        @click="handleClickMenuItem($event)"
                        @toggle="handleToggleItem($event)"
                      />
                    </div>
                  </div>
                </template>

                <!-- Workspace Menu View -->
                <template v-else-if="showWorkspaceMenu">
                  <div class="tw:overflow-y-auto tw:flex-1">
                    <div class="tw:flex tw:flex-col">
                      <div class="tw:flex tw:items-center tw:py-2 tw:mb-3">
                        <EdroneButton type="text" size="small" @click="handleBackToSettings">
                          <template #left-icon>
                            <font-awesome-icon icon="fa-light fa-arrow-left" />
                          </template>
                          {{ trans["Back"] }}
                        </EdroneButton>
                      </div>
                      <MainMenuWorkspaceContent class="tw:!w-full" />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { mainTree, MenuItem, settingsTree, getBadgeText } from "./MainMenu.helpers.ts";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { getMenuIndicators } from "vue-app/api/menu/menu";
import { DTOMenuIndicatorResponse } from "vue-app/api/dto";
import LiveEventsIcon from "../common/LiveEventsIcon.vue";
import MainMenuSettingsButton from "vue-app/components/MainMenu/MainMenuSettingsButton.vue";
import MainMenuMobileLinkList from "vue-app/components/MainMenu/MainMenuMobileLinkList.vue";
import MainMenuWorkspaceContent from "vue-app/components/MainMenu/MainMenuWorkspaceContent.vue";
import NotificationsMenu from "vue-app/components/NotificationsMenu/NotificationsMenu.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";

const isDev = window.EDRONE_CONTEXT.user.isDev;
const isMenuOpen = ref(false);
const showMenu = ref(false);
const showContent = ref(false);
const route = useRoute();
const router = useRouter();

const expandedItems = ref<Set<string>>(new Set());
const activeMenu = ref<string | null>(null);
const showSettingsMenu = ref<boolean>(false);
const showWorkspaceMenu = ref<boolean>(false);

let touchStartY = 0;
let touchStartTime = 0;
let scrollableElement: HTMLElement | null = null;
let initialScrollTop = 0;

const settingsItems = computed(() => settingsTree.value.filter((item) => !item.hidden || isDev));

const activeBackgroundStyle = "background-color: #e0e0003d; border: 1px solid #a3a30052;";

const isLiveEventsActive = computed(() => route.path.startsWith("/app/live-events"));
const isNewsletterHubActive = computed(() => route.path.startsWith("/app/newsletters"));
const isInboxActive = computed(() => route.path.startsWith("/app/inbox"));

const menuData = ref<DTOMenuIndicatorResponse | null>(null);
const inboxBadgeText = computed(() => getBadgeText("inbox", menuData.value));

const fetchMenuData = async () => {
  try {
    menuData.value = await getMenuIndicators();
  } catch (error) {
    console.error("Failed to fetch menu indicators:", error);
  }
};

let badgesInterval: ReturnType<typeof setInterval> | null = null;

const startBadgesInterval = () => {
  if (badgesInterval) {
    clearInterval(badgesInterval);
  }
  badgesInterval = setInterval(fetchMenuData, 30000);
};

const stopBadgesInterval = () => {
  if (badgesInterval) {
    clearInterval(badgesInterval);
    badgesInterval = null;
  }
};

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopBadgesInterval();
  } else {
    fetchMenuData();
    startBadgesInterval();
  }
};

const handlePopstate = () => {
  if (isMenuOpen.value) {
    isMenuOpen.value = false;
  }
};

onMounted(() => {
  fetchMenuData();
  startBadgesInterval();
  window.addEventListener("popstate", handlePopstate);
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  stopBadgesInterval();
  window.removeEventListener("popstate", handlePopstate);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

watch(
  () => isMenuOpen.value,
  async (value) => {
    if (value) {
      showMenu.value = true;
      await nextTick();
      showContent.value = true;
      if ("ontouchstart" in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)) {
        history.pushState(null, "");
      }
    } else {
      showContent.value = false;
      setTimeout(() => {
        showMenu.value = false;
        expandedItems.value.clear();
        activeMenu.value = null;
        showSettingsMenu.value = false;
        showWorkspaceMenu.value = false;
      }, 100);
    }
  }
);

function handleClickMenuItem(item: MenuItem) {
  gtagUniversalSend({ component_id: "main-menu__navigation-link-button--" + item.name });

  if (item.name === "logout") {
    logout();
    return;
  }
  if (item.disabled && !isDev) {
    return;
  } else if (item.name === "workspace") {
    showSettingsMenu.value = false;
    showWorkspaceMenu.value = true;
  } else if (item.children?.length) {
    handleToggleItem(item.name);
  } else if (item.path) {
    isMenuOpen.value = false;
  }
}

function handleToggleItem(itemName: string) {
  if (activeMenu.value === itemName) {
    // Close currently active menu
    activeMenu.value = null;
    expandedItems.value.clear();
  } else {
    // Close all menus and open the new one
    expandedItems.value.clear();
    activeMenu.value = itemName;
    expandedItems.value.add(itemName);
  }
}

function logout() {
  window.location.href = "/logout";
}

function handleClickSettingsButton() {
  showSettingsMenu.value = true;
}

function handleBackToMainMenu() {
  showSettingsMenu.value = false;
  showWorkspaceMenu.value = false;
}

function handleBackToSettings() {
  showWorkspaceMenu.value = false;
  showSettingsMenu.value = true;
}

function handleNavigate(path: string) {
  if (isMenuOpen.value) {
    isMenuOpen.value = false;
  }
  router.push(path);
}

function handleTouchStart(event: TouchEvent) {
  touchStartY = event.touches[0].clientY;
  touchStartTime = Date.now();

  const target = event.target as HTMLElement;
  scrollableElement = target.closest(".tw\\:overflow-y-auto");
  initialScrollTop = scrollableElement?.scrollTop || 0;
}

function handleTouchMove(event: TouchEvent) {
  const currentY = event.touches[0].clientY;
  const deltaY = currentY - touchStartY;

  if (deltaY > 0 && initialScrollTop === 0) {
    event.preventDefault();
  }
}

function handleTouchEnd(event: TouchEvent) {
  const touchEndY = event.changedTouches[0].clientY;
  const deltaY = touchEndY - touchStartY;
  const deltaTime = Date.now() - touchStartTime;

  const currentScrollTop = scrollableElement?.scrollTop || 0;
  const isAtTop = currentScrollTop === 0 && initialScrollTop === 0;

  const isSwipeDown = deltaY > 100;
  const isQuickSwipe = deltaTime < 300 && deltaY > 50;

  if (isAtTop && (isSwipeDown || isQuickSwipe)) {
    isMenuOpen.value = false;
    gtagUniversalSend({ component_id: "main-menu__mobile-swipe-close" });
  }
}
</script>

<style scoped>
.main-menu-mobile-backdrop {
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}
</style>
